import { z } from 'zod';

import {
  ROUNDING_MODE,
  ROUNDING_MODE_OPTIONS,
} from '#/utils/roundingMode';

const lineSchema = z.object({
  lineId: z.string().optional().nullable(),
  itemName: z.string().trim().min(1, '請填寫品名'),
  specification: z.string().optional().nullable(),
  qty: z.coerce.number({
    required_error: '請輸入數量',
    invalid_type_error: '數量必須為數字',
  }).positive('數量必須大於 0'),
  unitPrice: z.coerce.number({
    required_error: '請輸入單價',
    invalid_type_error: '單價必須為數字',
  }).min(0, '單價不可為負'),
  remark: z.string().optional().nullable(),
});

export const MISC_PURCHASE_ORDER_TAX_TYPE_OPTIONS = [
  { value: 'TAX_EXCLUDED', label: '應稅（未稅價）' },
  { value: 'TAX_INCLUDED', label: '含稅價' },
  { value: 'TAX_EXEMPT', label: '免稅' },
];

export const MISC_PURCHASE_ORDER_ROUNDING_MODE_OPTIONS = ROUNDING_MODE_OPTIONS;

export const MISC_PURCHASE_ORDER_FIELD_LABELS = {
  purchaseOrderDate: '採購日',
  supplierName: '供應商',
  currencyId: '幣別',
  paymentTermId: '付款條件',
  taxType: '稅別',
  roundingMode: '進位',
  issueReason: '採購事由',
  remark: '備註',
  lines: '明細',
  'lines.itemName': '品名',
  'lines.specification': '規格',
  'lines.qty': '數量',
  'lines.unitPrice': '單價',
  'lines.remark': '行備註',
};

export const EMPTY_MISC_PURCHASE_ORDER_LINE = {
  lineId: '',
  itemName: '',
  specification: '',
  qty: '',
  unitPrice: '',
  remark: '',
};

export const DEFAULT_MISC_PURCHASE_ORDER_FORM = {
  purchaseOrderDate: '',
  supplierName: '',
  currencyId: '',
  paymentTermId: '',
  taxType: 'TAX_EXCLUDED',
  roundingMode: ROUNDING_MODE.HALF_UP,
  issueReason: '',
  remark: '',
  lines: [{ ...EMPTY_MISC_PURCHASE_ORDER_LINE }],
};

export const miscPurchaseOrderFormSchema = z.object({
  purchaseOrderDate: z.string().optional().nullable(),
  supplierName: z.string().trim().min(1, '請填寫供應商'),
  currencyId: z.string().min(1, '請選擇幣別'),
  paymentTermId: z.string().optional().nullable(),
  taxType: z.enum(['TAX_EXCLUDED', 'TAX_INCLUDED', 'TAX_EXEMPT'], {
    message: '請選擇稅別',
  }),
  roundingMode: z.enum(
    [ROUNDING_MODE.UP, ROUNDING_MODE.DOWN, ROUNDING_MODE.HALF_UP],
    { message: '請選擇進位法' },
  ),
  issueReason: z.string().optional().nullable(),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema).min(1, '至少一筆明細'),
});

export function mapMiscPurchaseOrderDetailToForm(detail) {
  const lines = (detail?.lines ?? []).map((line) => ({
    lineId: line?.lineId ?? '',
    itemName: line?.itemName ?? '',
    specification: line?.specification ?? '',
    qty: line?.qty ?? '',
    unitPrice: line?.unitPrice ?? '',
    remark: line?.remark ?? '',
  }));

  return {
    purchaseOrderDate: detail?.purchaseOrderDate ?? '',
    supplierName: detail?.supplierName ?? '',
    currencyId: detail?.currencyId ?? detail?.currency?.id ?? '',
    paymentTermId: detail?.paymentTerm?.id ?? detail?.paymentTermId ?? '',
    taxType: detail?.amountSummary?.taxType ?? detail?.taxType ?? 'TAX_EXCLUDED',
    roundingMode: detail?.amountSummary?.roundingMode
      ?? detail?.roundingMode
      ?? ROUNDING_MODE.HALF_UP,
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: lines.length ? lines : [{ ...EMPTY_MISC_PURCHASE_ORDER_LINE }],
  };
}

export function pickMiscPurchaseOrderUpdatedAt(detail) {
  return detail?.record?.updatedAt ?? detail?.updatedAt ?? null;
}
